import { useSelectionStore } from '@/shared/state/selection/store'
import { useTimelineStore } from '@/features/timeline/stores/timeline-store'
import {
  analyzeFillerWordsForItems,
  asSilenceRangesByItemId,
  type FillerRemovalPresetKey,
  FILLER_REMOVAL_PRESETS,
} from '@/features/timeline/utils/filler-word-removal-preview'
import {
  analyzeSilenceForItems,
  DEFAULT_SILENCE_REMOVAL_SETTINGS,
} from '@/features/timeline/utils/silence-removal-preview'
import {
  applyFillerRemovalToItems,
  applySilenceRemovalToItems,
} from '@/features/timeline/stores/actions/silence-removal-actions'
import { ASSISTANT_TOOL_ERROR_CODES, AssistantToolError } from '@/features/assistant/tool-errors'
import type { TimelineItem } from '@/types/timeline'

function isAnalyzableTimelineItem(item: TimelineItem): boolean {
  return (item.type === 'audio' || item.type === 'video') && Boolean(item.mediaId)
}

function getTimelineItemsByIds(itemIds: readonly string[]) {
  const items = useTimelineStore.getState().items
  const itemsById = new Map(items.map((item) => [item.id, item] as const))
  return itemIds
    .map((itemId) => itemsById.get(itemId))
    .filter((item): item is TimelineItem => Boolean(item))
}

function resolveTargetItemIds(input?: {
  itemIds?: readonly string[]
  transcriptMediaId?: string
}, options?: {
  allowSelectionFallback?: boolean
}): string[] {
  const timelineItems = useTimelineStore.getState().items

  if (input?.itemIds?.length) {
    return [...new Set(input.itemIds)]
  }

  if (input?.transcriptMediaId) {
    return timelineItems
      .filter((item) => isAnalyzableTimelineItem(item) && item.mediaId === input.transcriptMediaId)
      .map((item) => item.id)
  }

  if (options?.allowSelectionFallback !== false) {
    const selectedItemIds = useSelectionStore.getState().selectedItemIds
    if (selectedItemIds.length > 0) {
      return selectedItemIds
    }
  }

  return []
}

function calculateTotalDurationSeconds(itemIds: readonly string[]): number {
  const timeline = useTimelineStore.getState()
  const fps = Math.max(1, timeline.fps)
  return getTimelineItemsByIds(itemIds)
    .filter(isAnalyzableTimelineItem)
    .reduce((sum, item) => sum + item.durationInFrames / fps, 0)
}

export function estimateTimelineCleanupSeconds(input?: {
  itemIds?: readonly string[]
  transcriptMediaId?: string
}): number {
  const targetItemIds = resolveTargetItemIds(input)
  return Math.max(1, Math.ceil(calculateTotalDurationSeconds(targetItemIds)))
}

function assertExplicitCleanupTarget(input: {
  itemIds?: readonly string[]
  transcriptMediaId?: string
}) {
  if (input.itemIds?.length || input.transcriptMediaId) {
    return
  }

  throw new AssistantToolError(
    ASSISTANT_TOOL_ERROR_CODES.invalidToolInput,
    'Cleanup tools invoked through MCP must include explicit target itemIds or transcriptMediaId.',
    {
      details: {
        requiresExplicitTarget: true,
      },
    },
  )
}

function summarizeRanges(
  rangesByItemId: Record<string, Array<{ durationFrames: number }>>,
) {
  const fps = Math.max(1, useTimelineStore.getState().fps)
  let affectedItemCount = 0
  let removedRangeCount = 0
  let totalRemovedFrames = 0

  for (const ranges of Object.values(rangesByItemId)) {
    if (ranges.length === 0) {
      continue
    }

    affectedItemCount += 1
    removedRangeCount += ranges.length
    totalRemovedFrames += ranges.reduce((sum, range) => sum + range.durationFrames, 0)
  }

  return {
    affectedItemCount,
    removedRangeCount,
    totalRemovedSeconds: totalRemovedFrames / fps,
  }
}

export async function removeFillerWordsWithAssistant(input: {
  itemIds?: readonly string[]
  transcriptMediaId?: string
  preset?: FillerRemovalPresetKey
  dryRun?: boolean
}, options?: {
  allowSelectionFallback?: boolean
}) {
  if (options?.allowSelectionFallback === false) {
    assertExplicitCleanupTarget(input)
  }

  const targetItemIds = resolveTargetItemIds(input, options)
  if (targetItemIds.length === 0) {
    throw new Error('No eligible timeline items are selected for filler-word removal.')
  }

  const preset =
    FILLER_REMOVAL_PRESETS.find((candidate) => candidate.key === (input.preset ?? 'balanced')) ??
    FILLER_REMOVAL_PRESETS.find((candidate) => candidate.key === 'balanced')!
  const rangesByItemId = await analyzeFillerWordsForItems(targetItemIds, preset.settings)
  const summary = summarizeRanges(rangesByItemId)

  if (input.dryRun !== false) {
    return {
      ...summary,
      dryRun: true,
    }
  }

  applyFillerRemovalToItems(targetItemIds, asSilenceRangesByItemId(rangesByItemId))

  return {
    ...summary,
    dryRun: false,
  }
}

export async function removeSilenceWithAssistant(input: {
  itemIds?: readonly string[]
  transcriptMediaId?: string
  thresholdDb?: number
  minSilenceMs?: number
  paddingMs?: number
  dryRun?: boolean
}, options?: {
  allowSelectionFallback?: boolean
}) {
  if (options?.allowSelectionFallback === false) {
    assertExplicitCleanupTarget(input)
  }

  const targetItemIds = resolveTargetItemIds(input, options)
  if (targetItemIds.length === 0) {
    throw new Error('No eligible timeline items are selected for silence removal.')
  }

  const rangesByItemId = await analyzeSilenceForItems(targetItemIds, {
    ...DEFAULT_SILENCE_REMOVAL_SETTINGS,
    thresholdDb: input.thresholdDb ?? DEFAULT_SILENCE_REMOVAL_SETTINGS.thresholdDb,
    minSilenceMs: input.minSilenceMs ?? DEFAULT_SILENCE_REMOVAL_SETTINGS.minSilenceMs,
    paddingMs: input.paddingMs ?? DEFAULT_SILENCE_REMOVAL_SETTINGS.paddingMs,
  })
  const summary = summarizeRanges(rangesByItemId)

  if (input.dryRun !== false) {
    return {
      ...summary,
      dryRun: true,
    }
  }

  applySilenceRemovalToItems(targetItemIds, rangesByItemId)

  return {
    ...summary,
    dryRun: false,
  }
}
